/* eslint-disable no-unused-vars */
import { TagHandlerBase } from './tagHandlerBase';
import store from '@/store';

/**
 * Handler for annotation tags
 */
export class AnnotationHandler extends TagHandlerBase {
    /**
     * Check if tag type should be processed
     * @param {string} type - Tag type
     * @param {Object} displaySettings - Display settings
     * @returns {boolean} Should process annotation
     */
    shouldProcess(type, displaySettings) {
        return displaySettings.showAnnotations && type === 'annotation';
    }
    
    /**
     * Collect annotation data
     * @param {Object} settings - Display settings
     * @param {Object} viewerData - Viewer position data
     * @returns {Object} Collected annotation data
     */
    collectData(settings, viewerData) {
        const { isGlobalSearchFilter, isGlobalSearchTabShown, displaySettings } = settings;
        const data = {};
        
        // Clear any existing cluster text meshes
        this.clearClusterTextMeshes();
        
        // Skip if not applicable
        const type = this.extension.currentTagType || 'annotation';
        if (!this.shouldProcess(type, displaySettings)) return data;
        
        // Get annotations for the current level
        let annotations = this.isViewer2d() 
            ? store.state.forgeService.getAnnotationsInLevel(store.state.selectedLevel) 
            : store.state.annotations;

        if (isGlobalSearchFilter) {
            let isolatedTags = store.getters['GET_ISOLATED_IDS'];    
            if (isolatedTags.size > 0) {
                annotations = annotations.filter(t => isolatedTags.has(t.id));
            }
        }
        
        // Update with editing tags if in 2D view
        if (this.isViewer2d()) {
            annotations = store.state.forgeService.updateListWithEditingTag(annotations);
            
            // Filter annotations before clustering
            annotations = annotations.filter(annotation => {
                // Skip if annotation is for a different level
                if (annotation.level2D && (!this.isViewer2d() || annotation.level2D != store.state.selectedLevel.name)) return false;
                
                // Comment out global search filtering for now
                /*
                // Skip if filtered out by global search
                if (isGlobalSearchTabShown && isGlobalSearchFilter && !store.state.filteredAnnotations.includes(annotation)) return false;
                */
                
                return true;
            });
                
            // Only apply clustering to filtered annotations in 2D view
            if (annotations.length > 0) {
                // Apply clustering logic
                const { items, clusters } = this.getTagsClustersAndItems2D(annotations, viewerData.position);
                
                // Process clusters
                this.processClusters(clusters, data, 'annotation-cluster');
                
                // Update to use filtered items
                annotations = items;
            }
        } else {
            // Filter annotations for 3D view
            annotations = annotations.filter(annotation => {
                // Comment out global search filtering for now
                /*
                // Skip if filtered out by global search
                if (isGlobalSearchTabShown && isGlobalSearchFilter && !store.state.filteredAnnotations.includes(annotation)) return false;
                */
                
                return true;
            });
        }
        
        annotations.forEach(annotation => {
            // Get point in viewer space
            let point = this.getPointForAnnotation(annotation);
            if (!point) return;
            
            // Create point cloud data
            const pcData = this.createAnnotationData(annotation, point);
            
            // Add to category collection
            const category = annotation.category;
            data[category] ? data[category].push(pcData) : data[category] = [pcData];
            
            // Draw tag area
            this.drawTagArea(annotation);
        });
        
        return data;
    }
    
    /**
     * Get point in viewer space for annotation
     * @param {Object} annotation - Annotation object
     * @returns {Object|null} Point in viewer space
     */
    getPointForAnnotation(annotation) {
        let point = this.isViewer2d() 
            ? store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(annotation.point) 
            : annotation.getPointInView();
        
        // Skip if outside crop box in 3D view
        if (!this.isViewer2d() && !store.state.forgeService.getMainExtension()?.isWithinCropBox(point)) {
            return null;
        }
        
        return point;
    }
    
    /**
     * Create annotation data for point cloud
     * @param {Object} annotation - Annotation object
     * @param {Object} point - Point in viewer space
     * @returns {Object} Point cloud data
     */
    createAnnotationData(annotation, point) {
        const _this = this;
        return {
            name: annotation.name,
            thumbnailURL: annotation.thumbnailUrl,
            object: annotation,
            point: point,
            onClick: (event) => {
                store.dispatch('FETCH_GENERAL_TAG_BY_ID', annotation.id).then(tag => {
                    store.state.forgeService.onClickOn3dIcon(tag, event, _this.extension.viewType);
                });
            }
        };
    }
}
